// src/services/realtime.ts
//
// Real-time updates over AWS IoT Core (MQTT over WebSocket).
//
// A logged-in user's Cognito *identity* is authorized to talk to IoT by TWO
// things, and IoT grants the intersection:
//   1. the identity-pool authenticated IAM role (defined in CloudFormation), and
//   2. an AWS IoT policy attached to the identity at runtime via AttachPolicy.
// Without (2), IoT silently refuses the WebSocket connection — so we attach it
// once per session before subscribing.
import AWS from "aws-sdk";
import { fetchAuthSession } from "aws-amplify/auth";
import { PubSub } from "@aws-amplify/pubsub";

const region = "us-east-1";
const iotEndpoint = process.env.REACT_APP_IOT_ENDPOINT || "";
const browserPolicyName = process.env.REACT_APP_IOT_BROWSER_POLICY_NAME || "";

let pubsubClient: PubSub | null = null;
let policyAttached = false;

function getPubSub(): PubSub {
  if (!pubsubClient) {
    // The default PubSub export signs the WSS handshake with SigV4 using the
    // Amplify-configured Cognito (identity pool) credentials.
    pubsubClient = new PubSub({
      region,
      endpoint: `wss://${iotEndpoint}/mqtt`,
    });
  }
  return pubsubClient;
}

/**
 * Attach the browser IoT policy to this user's Cognito identity. Idempotent and
 * safe to call repeatedly; we short-circuit after the first success per session.
 */
export async function attachIotPolicy(): Promise<void> {
  if (policyAttached) return;

  const session = await fetchAuthSession();
  const identityId = session.identityId;
  const credentials = session.credentials;
  if (!identityId || !credentials) {
    throw new Error("No Cognito identity/credentials available — is the user logged in?");
  }

  const iot = new AWS.Iot({
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
  });

  await iot.attachPolicy({ policyName: browserPolicyName, target: identityId }).promise();
  policyAttached = true;
}

export interface RealtimeSubscription {
  unsubscribe: () => void;
}

// Human labels for known dot-path leaves; anything else is prettified from camelCase.
const FIELD_LABELS: Record<string, string> = {
  commonName: "Common Name",
  systemCategory: "System Category",
  assetUrl: "Asset URL",
  dailyReportEmail: "Daily Report Email",
  zohoDealIdentifier: "Zoho Deal Identifier",
  timezone: "System Timezone",
  ums_env: "UMS Environment",
  activeUnitIndex: "Active Unit",
};

function labelForPath(path: string): string {
  const leaf = path.split(".").pop() || path;
  if (FIELD_LABELS[leaf]) return FIELD_LABELS[leaf];
  return leaf
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Build a human-readable toast string from a real-time change event, e.g.
 * "Common Name for BWIDEVINWHTWS01 was updated by Jane Smith".
 */
export function describeRealtimeEvent(data: any): string {
  if (!data) return "";
  const code = data.system_code || "this system";
  const by = data.actor ? ` by ${data.actor}` : "";

  if (data.entity === "assets") {
    // Live status patch (processing/syndication progress). Stay silent on routine
    // step advances; surface only terminal errors so they don't get missed.
    if (data.status) {
      if (data.status.processingStatus === "ERROR" || data.status.syndicationStatus === "ERROR") {
        const step = data.status.processingCurrentStep || data.status.syndicationCurrentStep || "processing";
        return `An asset in ${code} hit an error at ${step}`;
      }
      return "";
    }
    return `A new asset was added to ${code}`;
  }
  // Unit -> UMS run acknowledgement (systems/<code>/runStatus). The unit reports
  // whether it received the run command and whether it passed VIN validation.
  if (data.entity === "runStatus") {
    const unit = data.unit_index ? ` (unit ${data.unit_index})` : "";
    const vin = data.asset_id ? ` [${data.asset_id}]` : "";
    if (data.event === "rejected") {
      return `Run rejected${unit}${vin}: ${data.reason || "failed validation"}`;
    }
    if (data.event === "accepted") return `Run accepted by ${code}${unit}${vin}`;
    if (data.event === "received") return `Run received by ${code}${unit}${vin}`;
    return "";
  }
  if (data.entity === "cameras") {
    return `Cameras for ${code} were updated${by}`;
  }

  // system edits — name the field(s) from the diff
  const keys = [
    ...(data.changes ? Object.keys(data.changes) : []),
    ...(Array.isArray(data.removePaths) ? data.removePaths : []),
  ];
  if (keys.length === 1) return `${labelForPath(keys[0])} for ${code} was updated${by}`;
  if (keys.length > 1) return `${keys.length} fields for ${code} were updated${by}`;
  return `${code} was updated${by}`;
}

/**
 * Subscribe to a system's real-time change events. `onMessage` fires for every
 * message published under systems/<systemCode>/#.
 */
export async function subscribeToSystem(
  systemCode: string,
  onMessage: (data: unknown) => void,
  onError?: (err: unknown) => void,
  subtopic: string = "#"
): Promise<RealtimeSubscription> {
  await attachIotPolicy();

  // subtopic defaults to "#" (all events for the system). Pass e.g. "assets" to
  // only receive asset events on the Assets page.
  const topic = `systems/${systemCode}/${subtopic}`;
  const handle = getPubSub()
    .subscribe({ topics: [topic] })
    .subscribe({
      next: (data: unknown) => {
        // The systems/<code>/# subscription also sees unit downlink commands
        // (e.g. systems/<code>/run, which carries `command`). Those are for the
        // unit, not the browser — ignore them so they don't trigger a refetch/toast.
        if (data && typeof data === "object" && "command" in (data as Record<string, unknown>)) {
          return;
        }
        onMessage(data);
      },
      error: (err: unknown) => onError?.(err),
    });

  return { unsubscribe: () => handle.unsubscribe() };
}
